import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatPrice } from "../lib/api";
import { useCompare } from "../lib/compare";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { X, GitCompareArrows, ArrowLeft, Check, Minus } from "lucide-react";

const ROWS = [
    { key: "price", label: "Preis", fmt: (v) => formatPrice(v) },
    { key: "year", label: "Erstzulassung" },
    { key: "mileage", label: "Kilometerstand", fmt: (v) => (v ? `${Number(v).toLocaleString("de-DE")} km` : "—") },
    { key: "condition", label: "Zustand" },
    { key: "power_hp", label: "Leistung (PS)", fmt: (v) => (v ? `${v} PS` : "—") },
    { key: "power_kw", label: "Leistung (kW)", fmt: (v) => (v ? `${v} kW` : "—") },
    { key: "displacement", label: "Hubraum", fmt: (v) => (v ? `${v} ccm` : "—") },
    { key: "fuel", label: "Kraftstoff" },
    { key: "transmission", label: "Getriebe" },
    { key: "bike_type", label: "Fahrradtyp" },
    { key: "frame_size", label: "Rahmengröße" },
    { key: "wheel_size", label: "Laufradgröße" },
    { key: "color", label: "Farbe" },
    { key: "location", label: "Standort" },
    { key: "sale_type", label: "Verkaufsart", fmt: (v) => (v === "auction" ? "Auktion" : "Festpreis") },
    { key: "allow_offers", label: "Preisvorschlag", fmt: (v) => (v ? <Check className="w-4 h-4 text-green-600" /> : <Minus className="w-4 h-4 text-gray-300" />) },
    { key: "allow_tradein", label: "Inzahlungnahme", fmt: (v) => (v ? <Check className="w-4 h-4 text-green-600" /> : <Minus className="w-4 h-4 text-gray-300" />) },
];

export default function ComparePage() {
    const compare = useCompare();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all(
            compare.ids.map((id) => api.get(`/listings/${id}`).then((r) => r.data).catch(() => null))
        ).then((res) => {
            if (!cancelled) {
                setItems(res.filter(Boolean));
                setLoading(false);
            }
        });
        return () => { cancelled = true; };
    }, [compare.ids]);

    // Identify the best value per row to highlight (lowest price, lowest mileage, etc.)
    const bestForRow = (key) => {
        if (!["price", "mileage"].includes(key)) return null;
        const vals = items.map((it) => Number(it[key] ?? Infinity));
        const min = Math.min(...vals);
        if (!Number.isFinite(min)) return null;
        return min;
    };

    if (loading) {
        return <div className="max-w-7xl mx-auto py-10 px-6">Lädt…</div>;
    }

    if (items.length === 0) {
        return (
            <div className="max-w-3xl mx-auto py-16 px-6 text-center" data-testid="compare-empty">
                <GitCompareArrows className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h2 className="font-display text-2xl font-bold">Noch keine Fahrzeuge im Vergleich</h2>
                <p className="text-gray-600 mt-2 mb-6">Klicke auf das Vergleichs-Symbol bei einem Inserat, um es hier zu vergleichen.</p>
                <Button onClick={() => navigate("/search?category=motorcycle")} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider">
                    Zur Suche
                </Button>
            </div>
        );
    }

    return (
        <div className="bg-[#F8F9FA] min-h-screen pb-16" data-testid="compare-page">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                <button onClick={() => navigate(-1)} className="text-sm text-gray-600 hover:text-[#111827] inline-flex items-center gap-1 mb-4">
                    <ArrowLeft className="w-4 h-4" /> Zurück
                </button>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                    <div>
                        <Badge className="bg-[#2563EB] text-white hover:bg-[#2563EB] uppercase tracking-widest text-[10px] font-bold rounded-sm mb-2">Vergleich</Badge>
                        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">{items.length} Fahrzeuge im Vergleich</h1>
                    </div>
                    <Button onClick={compare.clear} variant="outline" className="rounded-sm font-bold border-[#DC2626] text-[#DC2626] hover:bg-red-50" data-testid="compare-clear-page">
                        Alle entfernen
                    </Button>
                </div>

                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full" data-testid="compare-table">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="text-left text-xs font-bold uppercase tracking-wider text-gray-500 px-4 py-3 sticky left-0 bg-gray-50 z-10 min-w-[180px]">Eigenschaft</th>
                                {items.map((it) => (
                                    <th key={it.id} className="px-4 py-3 text-left min-w-[240px]" data-testid={`compare-col-${it.id}`}>
                                        <div className="relative">
                                            <button
                                                onClick={() => compare.remove(it.id)}
                                                className="absolute -top-1 right-0 text-gray-400 hover:text-[#DC2626]"
                                                data-testid={`compare-table-remove-${it.id}`}
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                            <Link to={`/listing/${it.id}`} className="block" target="_blank" rel="noopener noreferrer">
                                                {it.images?.[0] && (
                                                    <img src={it.images[0]} alt={it.title} className="w-full aspect-[4/3] object-cover rounded-sm border border-gray-200 mb-2" />
                                                )}
                                                <div className="font-display font-bold text-sm leading-tight">{it.brand} {it.model}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{it.title}</div>
                                            </Link>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {ROWS.map((row) => {
                                const anyHas = items.some((it) => it[row.key] !== undefined && it[row.key] !== null && it[row.key] !== "");
                                if (!anyHas) return null;
                                const best = bestForRow(row.key);
                                return (
                                    <tr key={row.key} className="border-t border-gray-100">
                                        <td className="text-xs font-bold uppercase tracking-wider text-gray-500 px-4 py-3 sticky left-0 bg-white">{row.label}</td>
                                        {items.map((it) => {
                                            const raw = it[row.key];
                                            const display = row.fmt ? row.fmt(raw) : (raw ?? <span className="text-gray-300">—</span>);
                                            const isBest = best !== null && Number(raw) === best && items.length > 1;
                                            return (
                                                <td key={it.id} className={`px-4 py-3 text-sm ${isBest ? "bg-green-50 font-bold" : ""}`} data-testid={`compare-cell-${row.key}-${it.id}`}>
                                                    {isBest && <Badge className="bg-green-600 text-white hover:bg-green-600 text-[9px] font-bold rounded-sm mr-2 align-middle">BEST</Badge>}
                                                    {display}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            <tr className="border-t border-gray-100 bg-gray-50">
                                <td className="px-4 py-4 sticky left-0 bg-gray-50" />
                                {items.map((it) => (
                                    <td key={it.id} className="px-4 py-4">
                                        <Link to={`/listing/${it.id}`} target="_blank" rel="noopener noreferrer">
                                            <Button className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider text-xs w-full" data-testid={`compare-open-listing-${it.id}`}>
                                                Inserat öffnen →
                                            </Button>
                                        </Link>
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
